// Router definition — matches PAGE_ROUTES.md.
//
// Wave A Part 2: every authenticated route renders inside <AppShell> so
// the sidebar / topbar are always present. Public routes and the 404 page
// remain standalone.
//
// Wave E adds the admin / settings cluster:
//   - /admin/users + invite + :id + :id/edit
//   - /admin/drivers + create + :id + :id/edit
//   - /admin/roles
//   - /settings/{company,notifications,templates,products,work-sources,
//                payment-modes,time-slots,audit-log,data-export}
// Plus the QuickBooks OAuth callback (PUBLIC) and SubscriptionGate
// boundary markers around the gated features (data-export, custom-templates,
// audit-log, quickbooks).

import { createBrowserRouter, Navigate, Outlet, useParams } from 'react-router-dom';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { SuperProtectedRoute } from './auth/SuperProtectedRoute';
import { RoleGate } from './auth/RoleGate';
import { LoginPage } from './pages/auth/LoginPage';
import { SuperLoginPage } from './pages/super/auth/SuperLoginPage';
import { RealmSelectPage } from './pages/auth/RealmSelectPage';
import { NotFoundPage } from './pages/errors/NotFoundPage';
import { LogoutTrigger } from './pages/auth/LogoutTrigger';
import { AppShell } from './components/shell/AppShell';
import { SuperAppShell } from './components/shell/SuperAppShell';
import { BlankShell } from './components/shell/BlankShell';
import { ComingSoon } from './components/shell/ComingSoon';
import { ScrollToTop } from './components/shell/ScrollToTop';
import { HomePage } from './pages/home/HomePage';
import { CollectionsLayout } from './pages/collections/CollectionsLayout';
import { CollectionsDashboardPage } from './pages/collections/dashboard/CollectionsDashboardPage';
import { CollectionsDriverMapPage } from './pages/collections/drivers/CollectionsDriverMapPage';
import { GeoLinkPage } from './pages/collections/geolink/GeoLinkPage';
import { NearbyBookingsPage } from './pages/collections/geolink/NearbyBookingsPage';
import { CoverageAnalysisPage } from './pages/collections/geolink/CoverageAnalysisPage';
import { SkipLayout } from './pages/skip/SkipLayout';
import { SkipDashboardPage } from './pages/skip/dashboard/SkipDashboardPage';
import { SkipJobsListPage } from './pages/skip/jobs/SkipJobsListPage';
import { SkipJobDetailPage } from './pages/skip/jobs/SkipJobDetailPage';
import { SkipJobCreatePage } from './pages/skip/jobs/SkipJobCreatePage';
import { SkipJobEditPage } from './pages/skip/jobs/SkipJobEditPage';
import { SkipEnquiriesListPage } from './pages/skip/enquiries/SkipEnquiriesListPage';
import { SkipEnquiryDetailPage } from './pages/skip/enquiries/SkipEnquiryDetailPage';
import { SkipEnquiryCreatePage } from './pages/skip/enquiries/SkipEnquiryCreatePage';
import { SkipEnquiryEditPage } from './pages/skip/enquiries/SkipEnquiryEditPage';
import { ProcessEnquiryPage } from './pages/skip/enquiries/ProcessEnquiryPage';
import { SkipInventoryListPage } from './pages/skip/inventory/SkipInventoryListPage';
import { SkipInventoryDetailPage } from './pages/skip/inventory/SkipInventoryDetailPage';
import { SkipInventoryCreatePage } from './pages/skip/inventory/SkipInventoryCreatePage';
import { SkipInventoryEditPage } from './pages/skip/inventory/SkipInventoryEditPage';
import { SkipStockTypesPage } from './pages/skip/settings/SkipStockTypesPage';
import { SkipJobTypesPage } from './pages/skip/settings/SkipJobTypesPage';
import { SkipJobChooserPage } from './pages/skip/jobs/SkipJobChooserPage';
import { SkipActiveJobsPage } from './pages/skip/jobs/SkipActiveJobsPage';
import { SkipCustomersListPage } from './pages/skip/customers/SkipCustomersListPage';
import { SkipCustomerCreatePage } from './pages/skip/customers/SkipCustomerCreatePage';
import { SkipCustomerDetailPage } from './pages/skip/customers/SkipCustomerDetailPage';
import { SkipDriverMapPage } from './pages/skip/drivers/SkipDriverMapPage';
import { WeighbridgeLayout } from './pages/weighbridge/WeighbridgeLayout';
import { KioskRoot } from './pages/weighbridge/kiosk/KioskRoot';
import { WbDashboardPage } from './pages/weighbridge/dashboard/WbDashboardPage';
import { WbTicketsPage } from './pages/weighbridge/tickets/WbTicketsPage';
import { WbTicketDetailPage } from './pages/weighbridge/tickets/WbTicketDetailPage';
import { WbNewTicketPage } from './pages/weighbridge/tickets/WbNewTicketPage';
import { WbOpenTicketsPage } from './pages/weighbridge/tickets/WbOpenTicketsPage';
import { WbCustomersPage } from './pages/weighbridge/customers/WbCustomersPage';
import { WbCustomerDetailPage } from './pages/weighbridge/customers/WbCustomerDetailPage';
import { WbVehiclesPage } from './pages/weighbridge/vehicles/WbVehiclesPage';
import { WbVehicleDetailPage } from './pages/weighbridge/vehicles/WbVehicleDetailPage';
import { WbHauliersPage } from './pages/weighbridge/hauliers/WbHauliersPage';
import { WbHaulierDetailPage } from './pages/weighbridge/hauliers/WbHaulierDetailPage';
import { WbDriversPage } from './pages/weighbridge/drivers/WbDriversPage';
import { WbDriverDetailPage } from './pages/weighbridge/drivers/WbDriverDetailPage';
import { WbProductsPage } from './pages/weighbridge/products/WbProductsPage';
import { WbProductDetailPage } from './pages/weighbridge/products/WbProductDetailPage';
import { WbPriceBandsPage } from './pages/weighbridge/pricing/WbPriceBandsPage';
import { WbPriceBandDetailPage } from './pages/weighbridge/pricing/WbPriceBandDetailPage';
import { WbContractsPage } from './pages/weighbridge/pricing/WbContractsPage';
import { WbContractDetailPage } from './pages/weighbridge/pricing/WbContractDetailPage';
import { WbStockPage } from './pages/weighbridge/stock/WbStockPage';
import { WbReportsPage } from './pages/reports/weighbridge/WbReportsPage';
import { WbSettingsPage } from './pages/weighbridge/settings/WbSettingsPage';
import { WbYardsPage } from './pages/weighbridge/settings/WbYardsPage';
import { WbYardDetailPage } from './pages/weighbridge/settings/WbYardDetailPage';
import { WbWeighbridgesPage } from './pages/weighbridge/settings/WbWeighbridgesPage';
import { WbWeighbridgeDetailPage } from './pages/weighbridge/settings/WbWeighbridgeDetailPage';
import { WbCamerasPage } from './pages/weighbridge/settings/WbCamerasPage';
import { WbCameraDetailPage } from './pages/weighbridge/settings/WbCameraDetailPage';
import { WbBarriersPage } from './pages/weighbridge/settings/WbBarriersPage';
import { WbBarrierDetailPage } from './pages/weighbridge/settings/WbBarrierDetailPage';
import { WbAgentsPage } from './pages/weighbridge/settings/WbAgentsPage';
import { WbVatSettingsPage } from './pages/weighbridge/settings/WbVatSettingsPage';
import { WbCompliancePage } from './pages/weighbridge/settings/WbCompliancePage';
import { WbLockManagementPage } from './pages/weighbridge/settings/WbLockManagementPage';
import { WbProductGroupsPage } from './pages/weighbridge/settings/WbProductGroupsPage';
import { WbPrintTemplatesPage } from './pages/weighbridge/settings/WbPrintTemplatesPage';
import { DiaryPage } from './pages/diary/DiaryPage';
import { TodayDiaryPage } from './pages/diary/TodayDiaryPage';
import { DiaryResultsPage } from './pages/diary/DiaryResultsPage';
import { BookingsPage } from './pages/bookings/BookingsPage';
import { BookingCreatePage } from './pages/bookings/BookingCreatePage';
import { BookingDetailPage } from './pages/bookings/BookingDetailPage';
import { BookingEditPage } from './pages/bookings/BookingEditPage';
import { PendingPage } from './pages/pending/PendingPage';
import { PendingDetailPage } from './pages/pending/PendingDetailPage';
import { PendingEditPage } from './pages/pending/PendingEditPage';
import { PendingCreatePage } from './pages/pending/PendingCreatePage';
import { CancellationsPage } from './pages/cancellations/CancellationsPage';
import { CustomersListPage } from './pages/customers/CustomersListPage';
import { CustomerCreatePage } from './pages/customers/CustomerCreatePage';
import { CustomerDetailPage } from './pages/customers/CustomerDetailPage';
import { CustomerEditPage } from './pages/customers/CustomerEditPage';
import { CustomerDuplicatesPage } from './pages/customers/CustomerDuplicatesPage';
import { ReceiptsListPage } from './pages/receipts/ReceiptsListPage';
import { ReceiptDetailPage } from './pages/receipts/ReceiptDetailPage';
import { ReceiptDirectCreatePage } from './pages/receipts/ReceiptDirectCreatePage';
import { ReceiptEditPage } from './pages/receipts/ReceiptEditPage';
import { ReceiptFromBookingPage } from './pages/receipts/ReceiptFromBookingPage';
import { ReceiptsBulkExportPage } from './pages/reports/collections/ReceiptsBulkExportPage';
import { ReceiptApprovalPage } from './pages/receipts/ReceiptApprovalPage';
import { LookupPage } from './pages/lookup/LookupPage';
import { QuotePage } from './pages/quote/QuotePage';
import { InvoicesListPage } from './pages/invoices/InvoicesListPage';
import { InvoiceCreatePage } from './pages/invoices/InvoiceCreatePage';
import { InvoiceDetailPage } from './pages/invoices/InvoiceDetailPage';
import { InvoiceEditPage } from './pages/invoices/InvoiceEditPage';
import { InvoiceComparePage } from './pages/invoices/InvoiceComparePage';
import { PaymentRunPage } from './pages/payment-run/PaymentRunPage';
import { BACSGeneratePage } from './pages/bacs/BACSGeneratePage';
import { BACSHistoryPage } from './pages/bacs/BACSHistoryPage';
import { BACSRunDetailPage } from './pages/bacs/BACSRunDetailPage';
import { QuickBooksStatusPage } from './pages/collections/integrations/quickbooks/QuickBooksStatusPage';
import { QuickBooksSyncPage } from './pages/collections/integrations/quickbooks/QuickBooksSyncPage';
import { QuickBooksCallbackPage } from './pages/collections/integrations/quickbooks/QuickBooksCallbackPage';
import { QuickBooksSettingsPage } from './pages/collections/integrations/quickbooks/QuickBooksSettingsPage';
import { QuickBooksCustomerMappingPage } from './pages/collections/integrations/quickbooks/QuickBooksCustomerMappingPage';
import { QuickBooksVendorMappingPage } from './pages/collections/integrations/quickbooks/QuickBooksVendorMappingPage';
import { CollectionsSettingsPage } from './pages/collections/settings/CollectionsSettingsPage';
import { IntegrationsLandingPage } from './pages/collections/integrations/IntegrationsLandingPage';
import { ReportsPage } from './pages/reports/collections/ReportsPage';
import { ReportRunnerPage } from './pages/reports/collections/ReportRunnerPage';
import { MetricsPage } from './pages/reports/collections/MetricsPage';
import { ReportsLayout } from './pages/reports/ReportsLayout';
import { SettingsLayout } from './pages/settings/SettingsLayout';
import { ReportsDashboardPage } from './pages/reports/ReportsDashboardPage';
import { SkipReportsPage } from './pages/reports/skip/SkipReportsPage';
import { SkipJobsOverviewPage } from './pages/reports/skip/SkipJobsOverviewPage';
import { SkipEnquiryFunnelPage } from './pages/reports/skip/SkipEnquiryFunnelPage';
import { SkipInventoryUtilisationPage } from './pages/reports/skip/SkipInventoryUtilisationPage';
import { SkipCustomerRevenuePage } from './pages/reports/skip/SkipCustomerRevenuePage';
import { ProfilePage } from './pages/profile/ProfilePage';
import { SupportPage } from './pages/support/SupportPage';
import { ThemePickerPage } from './pages/settings/theme/ThemePickerPage';
import { HolidaysPage } from './pages/settings/holidays/HolidaysPage';
import { PushNotificationsPage } from './pages/settings/push/PushNotificationsPage';
// Wave G — superadmin
import { SuperDashboardPage } from './pages/super/SuperDashboardPage';
import { SuperCompaniesListPage } from './pages/super/SuperCompaniesListPage';
import { SuperCompanyDetailPage } from './pages/super/SuperCompanyDetailPage';
import { SuperCompanyCreatePage } from './pages/super/SuperCompanyCreatePage';
import { SuperAdminsListPage } from './pages/super/SuperAdminsListPage';
import { SuperAdminCreatePage } from './pages/super/SuperAdminCreatePage';
import { SuperSystemPage } from './pages/super/SuperSystemPage';
import { SuperLogsPage } from './pages/super/logs/SuperLogsPage';
import { SuperWbActivityPage } from './pages/super/WbActivityPage';
import { SuperProductGroupsPage } from './pages/super/SuperProductGroupsPage';
import { CancelBookingPage } from './pages/public/cancel/CancelBookingPage';
import ReceiptDownloadPage from './pages/public/receipt-download/ReceiptDownloadPage';
import RouteErrorBoundary from './components/ui/RouteErrorBoundary/RouteErrorBoundary';
// Wave E — admin + settings cluster
import { UsersListPage } from './pages/admin/users/UsersListPage';
import { UserInvitePage } from './pages/admin/users/UserInvitePage';
import { UserDetailPage } from './pages/admin/users/UserDetailPage';
import { UserEditPage } from './pages/admin/users/UserEditPage';
import { DriversListPage } from './pages/admin/drivers/DriversListPage';
import { DriverCreatePage } from './pages/admin/drivers/DriverCreatePage';
import { DriverDetailPage } from './pages/admin/drivers/DriverDetailPage';
import { DriverEditPage } from './pages/admin/drivers/DriverEditPage';
import { RolesPage } from './pages/admin/roles/RolesPage';
import { AdminIndexPage } from './pages/admin/AdminIndexPage';
import { SettingsIndexPage } from './pages/settings/SettingsIndexPage';
import { CompanySettingsPage } from './pages/settings/company/CompanySettingsPage';
import { NotificationSettingsPage } from './pages/settings/notifications/NotificationSettingsPage';
import { TemplatesListPage } from './pages/settings/templates/TemplatesListPage';
import { TemplateEditPage } from './pages/settings/templates/TemplateEditPage';
import { PrintTemplatesListPage } from './pages/settings/print-templates/PrintTemplatesListPage';
import { PrintTemplateBuilderPage } from './pages/settings/print-templates/PrintTemplateBuilderPage';
import {
  ProductsPage,
  WorkSourcesPage,
  PaymentModesPage,
} from './pages/settings/catalogues/CataloguePage';
import { TimeSlotsPage } from './pages/settings/time-slots/TimeSlotsPage';
import { AuditLogPage } from './pages/reports/collections/audit-log/AuditLogPage';
import { DataExportPage } from './pages/reports/collections/data-export/DataExportPage';
import { SuperCompanyStoragePage } from './pages/super/companies/SuperCompanyStoragePage';
import { QuickLinksSettingsPage } from './pages/settings/quick-links/QuickLinksSettingsPage';
import { DwtSettingsPage } from './pages/settings/dwt/DwtSettingsPage';
import { SubscriptionGate } from './components/SubscriptionGate/SubscriptionGate';

const ANY_STAFF = ['staff', 'admin'] as const;
const ADMINS = ['admin'] as const;
const MANAGERS = ['manager'] as const;

function RootLayout() {
  return (
    <>
      <ScrollToTop />
      <Outlet />
    </>
  );
}

// Param-preserving redirect for the legacy `/reports/run/:reportId` URL — the
// report runner now lives under the Reports module at
// `/reports/collections/run/:reportId`.
function ReportRunRedirect() {
  const { reportId = '' } = useParams<{ reportId: string }>();
  return <Navigate to={`/reports/collections/run/${reportId}`} replace />;
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
  // -- Public routes (no shell) ---------------------------------------------
  { path: '/login', element: <LoginPage /> },
  // Wave I — platform super-admin login. Public; the SuperProtectedRoute
  // branch below redirects unauthenticated /super/* visits here.
  { path: '/super/login', element: <SuperLoginPage /> },
  { path: '/realm-select', element: <RealmSelectPage /> },
  // Weighbridge Kiosk Mode — full-screen touch kiosk. Lives OUTSIDE
  // ProtectedRoute: it owns its own session (kioskStore) + login surface
  // (PIN / password), so it must not be redirected to /login when the main
  // app session is anonymous.
  { path: '/weighbridge/kiosk', element: <KioskRoot /> },
  { path: '/auth/forgot', element: <ComingSoonStandalone name="Forgot Password" /> },
  { path: '/auth/reset', element: <ComingSoonStandalone name="Reset Password" /> },
  // Subdomain rebuild — public cancellation page. The server cancellation
  // token URL is `${PUBLIC_APP_URL}/cancel/{token}` (cancellationToken.ts),
  // so we wire `/cancel/:token` directly. Legacy `/p/*` paths kept as
  // ComingSoon placeholders for any pre-existing references.
  { path: '/cancel/:token', element: <CancelBookingPage /> },
  { path: '/p/cancel/:token', element: <CancelBookingPage /> },
  { path: '/p/receipt/:token', element: <ComingSoonStandalone name="Receipt" /> },
  { path: '/r/:token', element: <ReceiptDownloadPage /> },
  // Wave E — Intuit OAuth callback. PUBLIC because the user is bouncing
  // back from intuit.com before the SPA's session is restored.
  {
    path: '/collections/integrations/quickbooks/oauth/callback',
    element: <QuickBooksCallbackPage />,
  },
  { path: '/logout', element: <LogoutTrigger /> },

  // -- Authenticated --------------------------------------------------------
  // Two sibling layout routes inside ProtectedRoute:
  //   1. <BlankShell /> — chrome-less full-screen canvas. Hosts ONLY the
  //      module selector at `/` (and the `/home` alias). HomePage fills the
  //      viewport with the Tricivo wordmark + three module cards.
  //   2. <AppShell />   — the persistent sidebar + topbar layout used by
  //      every module page.
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <BlankShell />,
        children: [
          {
            element: (
              <RoleGate roles={[...ANY_STAFF]}>
                <Outlet />
              </RoleGate>
            ),
            children: [
              { path: '/', element: <HomePage /> },
              { path: '/home', element: <Navigate to="/" replace /> },
            ],
          },
        ],
      },
      {
        element: <AppShell />,
        children: [
          // GeoLink now lives INSIDE the Collections module (hub at
          // /collections/geolink). Legacy /geolink/* paths redirect there —
          // see the redirect routes in the Collections subtree below.
          // Skip Management module — owns /skip/*. SkipLayout pushes
          // <SkipNav /> into the sidebar slot for this subtree.
          {
            element: <SkipLayout />,
            children: [
              {
                element: (
                  <RoleGate roles={[...ANY_STAFF]}>
                    <Outlet />
                  </RoleGate>
                ),
                children: [
                  { path: '/skip/dashboard', element: <SkipDashboardPage /> },
                  { path: '/skip/jobs', element: <SkipJobsListPage /> },
                  // Literal segments must precede /:id parametric in v6 ranking;
                  // they're already exact matches but listing them first keeps
                  // the order obvious for human readers.
                  { path: '/skip/jobs/new', element: <SkipJobChooserPage /> },
                  { path: '/skip/jobs/active', element: <SkipActiveJobsPage /> },
                  { path: '/skip/jobs/create', element: <SkipJobCreatePage /> },
                  { path: '/skip/jobs/:id', element: <SkipJobDetailPage /> },
                  { path: '/skip/jobs/:id/edit', element: <SkipJobEditPage /> },
                  { path: '/skip/enquiries', element: <SkipEnquiriesListPage /> },
                  { path: '/skip/enquiries/create', element: <SkipEnquiryCreatePage /> },
                  { path: '/skip/enquiries/:id', element: <SkipEnquiryDetailPage /> },
                  { path: '/skip/enquiries/:id/edit', element: <SkipEnquiryEditPage /> },
                  { path: '/skip/enquiries/:id/process', element: <ProcessEnquiryPage /> },
                  { path: '/skip/inventory', element: <SkipInventoryListPage /> },
                  {
                    path: '/skip/inventory/create',
                    element: (
                      <RoleGate roles={[...ADMINS]}>
                        <SkipInventoryCreatePage />
                      </RoleGate>
                    ),
                  },
                  { path: '/skip/inventory/:id', element: <SkipInventoryDetailPage /> },
                  { path: '/skip/inventory/:id/edit', element: <SkipInventoryEditPage /> },
                  { path: '/skip/customers', element: <SkipCustomersListPage /> },
                  { path: '/skip/customers/create', element: <SkipCustomerCreatePage /> },
                  { path: '/skip/customers/:id', element: <SkipCustomerDetailPage /> },
                  // Stock types + sizes settings page (staff+ per §7.11 pattern).
                  {
                    path: '/skip/settings/stock-types',
                    element: <SkipStockTypesPage />,
                  },
                  // Job types + stages + actions settings page (staff+).
                  {
                    path: '/skip/settings/job-types',
                    element: <SkipJobTypesPage />,
                  },
                  // Skip reports relocated to the Reports module.
                  {
                    path: '/skip/reports',
                    element: <Navigate to="/reports/skip-management" replace />,
                  },
                  { path: '/skip/drivers/map', element: <SkipDriverMapPage /> },
                ],
              },
            ],
          },
          // Weighbridge Management module — owns /weighbridge/*.
          // C1 wave: every URL in the WB nav resolves to a real page
          // component (most are WbPlaceholder stubs that later phases
          // replace one at a time).
          {
            element: <WeighbridgeLayout />,
            children: [
              // Bare /weighbridge → /weighbridge/dashboard so the nav's
              // module-root link always lands somewhere meaningful.
              { path: '/weighbridge', element: <Navigate to="/weighbridge/dashboard" replace /> },
              // Staff-accessible routes.
              {
                element: (
                  <RoleGate roles={[...ANY_STAFF]}>
                    <Outlet />
                  </RoleGate>
                ),
                children: [
                  { path: '/weighbridge/dashboard', element: <WbDashboardPage /> },
                  // Tickets — static segments BEFORE the parametric :enc_id
                  // route so Express-style ordering matches the v6 ranking
                  // we already rely on elsewhere (see /pending /new vs /:id).
                  { path: '/weighbridge/tickets', element: <WbTicketsPage /> },
                  { path: '/weighbridge/tickets/new', element: <WbNewTicketPage /> },
                  { path: '/weighbridge/tickets/open', element: <WbOpenTicketsPage /> },
                  { path: '/weighbridge/tickets/:enc_id', element: <WbTicketDetailPage /> },
                  // Party + catalog (Phase C3).
                  { path: '/weighbridge/customers', element: <WbCustomersPage /> },
                  { path: '/weighbridge/customers/:encId', element: <WbCustomerDetailPage /> },
                  { path: '/weighbridge/vehicles', element: <WbVehiclesPage /> },
                  { path: '/weighbridge/vehicles/:enc_id', element: <WbVehicleDetailPage /> },
                  { path: '/weighbridge/hauliers', element: <WbHauliersPage /> },
                  { path: '/weighbridge/hauliers/:enc_id', element: <WbHaulierDetailPage /> },
                  { path: '/weighbridge/drivers', element: <WbDriversPage /> },
                  { path: '/weighbridge/drivers/:enc_id', element: <WbDriverDetailPage /> },
                  { path: '/weighbridge/products', element: <WbProductsPage /> },
                  { path: '/weighbridge/products/:enc_id', element: <WbProductDetailPage /> },
                  // Pricing (Phase C4) — admin-scoped page set, but the
                  // landing list pages stay staff-readable for visibility
                  // into bands/contracts; write operations are gated
                  // inside the page in later phases.
                  { path: '/weighbridge/pricing/bands', element: <WbPriceBandsPage /> },
                  { path: '/weighbridge/pricing/bands/:enc_id', element: <WbPriceBandDetailPage /> },
                  { path: '/weighbridge/pricing/contracts', element: <WbContractsPage /> },
                  { path: '/weighbridge/pricing/contracts/:enc_id', element: <WbContractDetailPage /> },
                  // Stock + reports.
                  { path: '/weighbridge/stock', element: <WbStockPage /> },
                  // Weighbridge reports relocated to the Reports module.
                  { path: '/weighbridge/reports', element: <Navigate to="/reports/weighbridge" replace /> },
                  // Settings hub — staff-readable. The page itself locks
                  // each card visually for non-admins; per-area pages below
                  // remain admin-only via the ADMINS RoleGate.
                  { path: '/weighbridge/settings', element: <WbSettingsPage /> },
                ],
              },
              // Admin-only settings cluster.
              {
                element: (
                  <RoleGate roles={[...ADMINS]}>
                    <Outlet />
                  </RoleGate>
                ),
                children: [
                  { path: '/weighbridge/settings/yards', element: <WbYardsPage /> },
                  { path: '/weighbridge/settings/yards/:enc_id', element: <WbYardDetailPage /> },
                  { path: '/weighbridge/settings/weighbridges', element: <WbWeighbridgesPage /> },
                  { path: '/weighbridge/settings/weighbridges/:enc_id', element: <WbWeighbridgeDetailPage /> },
                  { path: '/weighbridge/settings/cameras', element: <WbCamerasPage /> },
                  { path: '/weighbridge/settings/cameras/:enc_id', element: <WbCameraDetailPage /> },
                  { path: '/weighbridge/settings/barriers', element: <WbBarriersPage /> },
                  { path: '/weighbridge/settings/barriers/:enc_id', element: <WbBarrierDetailPage /> },
                  { path: '/weighbridge/settings/agents', element: <WbAgentsPage /> },
                  { path: '/weighbridge/settings/vat', element: <WbVatSettingsPage /> },
                  { path: '/weighbridge/settings/compliance', element: <WbCompliancePage /> },
                  { path: '/weighbridge/settings/print-templates', element: <WbPrintTemplatesPage /> },
                ],
              },
              // Lock management — admin + manager (migration 167). Force-release
              // stuck ticket locks; sits below the admin-only settings cluster.
              {
                element: (
                  <RoleGate roles={[...ADMINS, ...MANAGERS]}>
                    <Outlet />
                  </RoleGate>
                ),
                children: [
                  { path: '/weighbridge/settings/locks', element: <WbLockManagementPage /> },
                  { path: '/weighbridge/settings/product-groups', element: <WbProductGroupsPage /> },
                ],
              },
            ],
          },
          // Reports module — owns /reports/*. ReportsLayout pushes the
          // module-grouped, collapsible <ReportsNav /> into the sidebar slot.
          // Phase 1 consolidates the standalone report / summary / export
          // pages that previously lived under Collections / Skip / Weighbridge.
          {
            element: <ReportsLayout />,
            children: [
              // Staff-readable: dashboard + the Collections reports tab shell +
              // Skip placeholder + Weighbridge reports (WB tier gated).
              {
                element: (
                  <RoleGate roles={[...ANY_STAFF]}>
                    <Outlet />
                  </RoleGate>
                ),
                children: [
                  { path: '/reports', element: <ReportsDashboardPage /> },
                  { path: '/reports/collections', element: <ReportsPage /> },
                  {
                    path: '/reports/weighbridge',
                    element: (
                      <SubscriptionGate feature="weighbridge">
                        <WbReportsPage />
                      </SubscriptionGate>
                    ),
                  },
                ],
              },
              // Admin-only: report runner + metrics + bulk/data exports +
              // audit log. SubscriptionGates preserved from the old routes.
              {
                element: (
                  <RoleGate roles={[...ADMINS]}>
                    <Outlet />
                  </RoleGate>
                ),
                children: [
                  { path: '/reports/collections/run/:reportId', element: <ReportRunnerPage /> },
                  { path: '/reports/collections/metrics', element: <MetricsPage /> },
                  { path: '/reports/collections/receipts-export', element: <ReceiptsBulkExportPage /> },
                  {
                    path: '/reports/collections/audit-log',
                    element: (
                      <SubscriptionGate feature="audit-log">
                        <AuditLogPage />
                      </SubscriptionGate>
                    ),
                  },
                  {
                    path: '/reports/collections/data-export',
                    element: (
                      <SubscriptionGate feature="data-export">
                        <DataExportPage />
                      </SubscriptionGate>
                    ),
                  },
                ],
              },
              // Admin + manager only: Skip Management reports. A deliberately
              // tighter gate than the rest of the module (which admits plain
              // staff) — matches the server router's requireRole gate.
              {
                element: (
                  <RoleGate roles={['admin', 'manager']}>
                    <Outlet />
                  </RoleGate>
                ),
                children: [
                  { path: '/reports/skip-management', element: <SkipReportsPage /> },
                  { path: '/reports/skip-management/jobs', element: <SkipJobsOverviewPage /> },
                  { path: '/reports/skip-management/enquiries', element: <SkipEnquiryFunnelPage /> },
                  { path: '/reports/skip-management/inventory', element: <SkipInventoryUtilisationPage /> },
                  { path: '/reports/skip-management/customers', element: <SkipCustomerRevenuePage /> },
                ],
              },
            ],
          },
          // Settings module — owns /settings/*, /profile, /account/*,
          // and /support. SettingsLayout pushes <SettingsNav /> into the
          // sidebar slot for this subtree.
          {
            element: <SettingsLayout />,
            children: [
              {
                element: (
                  <RoleGate roles={[...ANY_STAFF]}>
                    <Outlet />
                  </RoleGate>
                ),
                children: [
                  { path: '/settings', element: <SettingsIndexPage /> },
                  { path: '/settings/company', element: <CompanySettingsPage /> },
                  { path: '/settings/notifications', element: <NotificationSettingsPage /> },
                  {
                    path: '/settings/templates',
                    element: (
                      <SubscriptionGate feature="custom-templates">
                        <TemplatesListPage />
                      </SubscriptionGate>
                    ),
                  },
                  {
                    path: '/settings/templates/:id',
                    element: (
                      <SubscriptionGate feature="custom-templates">
                        <TemplateEditPage />
                      </SubscriptionGate>
                    ),
                  },
                  { path: '/settings/print-templates', element: <PrintTemplatesListPage /> },
                  {
                    path: '/settings/print-templates/:templateType/edit',
                    element: (
                      <RoleGate roles={[...ADMINS]}>
                        <PrintTemplateBuilderPage />
                      </RoleGate>
                    ),
                  },
                  { path: '/settings/products', element: <ProductsPage /> },
                  { path: '/settings/work-sources', element: <WorkSourcesPage /> },
                  { path: '/settings/payment-modes', element: <PaymentModesPage /> },
                  { path: '/settings/time-slots', element: <TimeSlotsPage /> },
                  { path: '/settings/push', element: <PushNotificationsPage /> },
                  { path: '/profile', element: <ProfilePage /> },
                  { path: '/account/profile', element: <ProfilePage /> },
                  { path: '/account/password', element: <ProfilePage /> },
                  { path: '/account/usage', element: <ComingSoon name="Usage" /> },
                  { path: '/support', element: <SupportPage /> },
                  { path: '/support/:id', element: <ComingSoon name="Support Ticket" /> },
                  {
                    element: (
                      <RoleGate roles={[...ADMINS]}>
                        <Outlet />
                      </RoleGate>
                    ),
                    children: [
                      { path: '/settings/holidays', element: <HolidaysPage /> },
                      { path: '/settings/theme', element: <ThemePickerPage /> },
                      { path: '/settings/quick-links', element: <QuickLinksSettingsPage /> },
                      { path: '/settings/dwt', element: <DwtSettingsPage /> },
                      { path: '/settings/staff/*', element: <Navigate to="/admin/users" replace /> },
                      { path: '/settings/drivers/*', element: <Navigate to="/admin/drivers" replace /> },
                      { path: '/settings/message-templates', element: <ComingSoon name="Message Templates" /> },
                      { path: '/settings/message-templates/:id/edit', element: <ComingSoon name="Edit Message Template" /> },
                      { path: '/settings/communication-channels', element: <ComingSoon name="Communication Channels" /> },
                      {
                        path: '/settings/audit-log',
                        element: <Navigate to="/reports/collections/audit-log" replace />,
                      },
                      {
                        path: '/settings/data-export',
                        element: <Navigate to="/reports/collections/data-export" replace />,
                      },
                    ],
                  },
                ],
              },
            ],
          },
          // Collections module — owns every other authenticated route.
          // CollectionsLayout pushes <CollectionsNav /> into the sidebar
          // slot for this entire subtree (staff / admin / super /
          // manager role-gated children all get the same nav).
          {
            element: <CollectionsLayout />,
            errorElement: <RouteErrorBoundary />,
            children: [
              {
            element: (
              <RoleGate roles={[...ANY_STAFF]}>
                <Outlet />
              </RoleGate>
            ),
            children: [
              { path: '/collections/dashboard', element: <CollectionsDashboardPage /> },
              { path: '/collections/diary', element: <DiaryPage /> },
              { path: '/collections/diary/results', element: <DiaryResultsPage /> },
              { path: '/collections/diary/today', element: <TodayDiaryPage /> },
              { path: '/collections/lookup', element: <LookupPage /> },
              { path: '/collections/quote', element: <QuotePage /> },
              { path: '/collections/bookings', element: <BookingsPage /> },
              { path: '/collections/bookings/create', element: <BookingCreatePage /> },
              { path: '/collections/bookings/:enc_id', element: <BookingDetailPage /> },
              { path: '/collections/bookings/:enc_id/edit', element: <BookingEditPage /> },
              { path: '/collections/pending', element: <PendingPage /> },
              // /pending/new must be declared BEFORE /pending/:id so the
              // literal segment matches first; v6 does this by ranking but
              // listing the literal route first keeps the order obvious.
              { path: '/collections/pending/new', element: <PendingCreatePage /> },
              { path: '/collections/pending/:id', element: <PendingDetailPage /> },
              { path: '/collections/pending/:id/edit', element: <PendingEditPage /> },
              { path: '/customers', element: <CustomersListPage /> },
              { path: '/customers/new', element: <CustomerCreatePage /> },
              { path: '/customers/:id', element: <CustomerDetailPage /> },
              { path: '/customers/:id/edit', element: <CustomerEditPage /> },
              { path: '/collections/cancellations', element: <CancellationsPage /> },
              { path: '/collections/receipts', element: <ReceiptsListPage /> },
              { path: '/collections/receipts/new', element: <ReceiptDirectCreatePage /> },
              // Literal `/approval` is registered BEFORE `/:enc_id` so it
              // does not get captured as a detail-page id.
              { path: '/collections/receipts/approval', element: <ReceiptApprovalPage /> },
              { path: '/collections/receipts/:enc_id', element: <ReceiptDetailPage /> },
              {
                path: '/collections/bookings/:enc_id/receipt/new',
                element: <ReceiptFromBookingPage />,
              },
              { path: '/collections/invoices', element: <InvoicesListPage /> },
              { path: '/collections/invoices/compare', element: <InvoiceComparePage /> },
              { path: '/collections/invoices/:enc_id', element: <InvoiceDetailPage /> },
              { path: '/collections/payment-run', element: <PaymentRunPage /> },
              { path: '/activity-log', element: <ComingSoon name="Activity Log" /> },
              { path: '/collections/settings', element: <CollectionsSettingsPage /> },
              // Integrations landing — readable by any staff. Cards inside
              // self-lock when the user lacks admin role.
              { path: '/collections/integrations', element: <IntegrationsLandingPage /> },
              // GeoLink — hub + its three surfaces, now under Collections.
              // The Driver Map (formerly /drivers/map) lives inside the hub.
              { path: '/collections/geolink', element: <GeoLinkPage /> },
              { path: '/collections/geolink/driver-map', element: <CollectionsDriverMapPage /> },
              { path: '/collections/geolink/nearby', element: <NearbyBookingsPage /> },
              { path: '/collections/geolink/coverage', element: <CoverageAnalysisPage /> },
              // Legacy redirects — preserve any bookmarks / inbound links
              // against the old standalone GeoLink + Driver Map paths.
              { path: '/geolink', element: <Navigate to="/collections/geolink" replace /> },
              { path: '/geolink/nearby', element: <Navigate to="/collections/geolink/nearby" replace /> },
              { path: '/geolink/coverage', element: <Navigate to="/collections/geolink/coverage" replace /> },
              { path: '/drivers/map', element: <Navigate to="/collections/geolink/driver-map" replace /> },
            ],
          },
          {
            element: (
              <RoleGate roles={[...ADMINS]}>
                <Outlet />
              </RoleGate>
            ),
            children: [
              { path: '/customers/duplicates', element: <CustomerDuplicatesPage /> },
              { path: '/collections/receipts/:enc_id/edit', element: <ReceiptEditPage /> },
              // Bulk receipt export relocated to the Reports module.
              { path: '/collections/receipts/bulk-export', element: <Navigate to="/reports/collections/receipts-export" replace /> },
              { path: '/collections/invoices/new', element: <InvoiceCreatePage /> },
              { path: '/collections/invoices/create', element: <InvoiceCreatePage /> },
              { path: '/collections/invoices/:enc_id/edit', element: <InvoiceEditPage /> },
              { path: '/collections/bacs', element: <BACSHistoryPage /> },
              { path: '/collections/bacs/history', element: <BACSHistoryPage /> },
              { path: '/collections/bacs/generate', element: <BACSGeneratePage /> },
              { path: '/collections/bacs/:enc_id', element: <BACSRunDetailPage /> },
              { path: '/collections/bacs/runs/:enc_id', element: <BACSRunDetailPage /> },
              { path: '/collections/bacs/templates', element: <ComingSoon name="BACS Templates" /> },
              { path: '/collections/bacs/templates/:id/edit', element: <ComingSoon name="Edit BACS Template" /> },
              // Wave E follow-up: legacy bookmarks against /audit-log
              // continue to resolve by redirecting into the canonical
              // settings sub-tree.
              {
                path: '/audit-log',
                element: <Navigate to="/reports/collections/audit-log" replace />,
              },
              // Metrics + report runner relocated to the Reports module.
              { path: '/metrics', element: <Navigate to="/reports/collections/metrics" replace /> },
              { path: '/reports/run/:reportId', element: <ReportRunRedirect /> },
              // Wave E — admin landing + sub-pages (users / drivers / roles).
              { path: '/admin', element: <AdminIndexPage /> },
              { path: '/admin/users', element: <UsersListPage /> },
              { path: '/admin/users/invite', element: <UserInvitePage /> },
              { path: '/admin/users/:id', element: <UserDetailPage /> },
              { path: '/admin/users/:id/edit', element: <UserEditPage /> },
              { path: '/admin/drivers', element: <DriversListPage /> },
              { path: '/admin/drivers/create', element: <DriverCreatePage /> },
              { path: '/admin/drivers/:id', element: <DriverDetailPage /> },
              { path: '/admin/drivers/:id/edit', element: <DriverEditPage /> },
              { path: '/admin/roles', element: <RolesPage /> },
              {
                path: '/collections/integrations/quickbooks',
                element: (
                  <SubscriptionGate feature="quickbooks">
                    <QuickBooksStatusPage />
                  </SubscriptionGate>
                ),
              },
              {
                path: '/collections/integrations/quickbooks/sync',
                element: (
                  <SubscriptionGate feature="quickbooks">
                    <QuickBooksSyncPage />
                  </SubscriptionGate>
                ),
              },
              {
                path: '/collections/integrations/quickbooks/settings',
                element: (
                  <SubscriptionGate feature="quickbooks">
                    <QuickBooksSettingsPage />
                  </SubscriptionGate>
                ),
              },
              {
                path: '/collections/integrations/quickbooks/customers',
                element: (
                  <SubscriptionGate feature="quickbooks">
                    <QuickBooksCustomerMappingPage />
                  </SubscriptionGate>
                ),
              },
              {
                path: '/collections/integrations/quickbooks/vendors',
                element: (
                  <SubscriptionGate feature="quickbooks">
                    <QuickBooksVendorMappingPage />
                  </SubscriptionGate>
                ),
              },
            ],
          },
          {
            element: (
              <RoleGate roles={[...MANAGERS]}>
                <Outlet />
              </RoleGate>
            ),
            children: [
              { path: '/skips', element: <ComingSoon name="Skip Module" /> },
            ],
          },
            ],
          },
        ],
      },
    ],
  },

  // -- Wave I — Platform super-admin subtree --------------------------------
  // Sibling to ProtectedRoute, NOT nested inside it: super sessions live in
  // their own store and never share auth state with the tenant flow. The
  // SuperAppShell is a fully standalone shell — its own top bar and side
  // nav, no tenant SidebarContext / authStore branding bleed.
  {
    element: <SuperProtectedRoute />,
    children: [
      {
        element: <SuperAppShell />,
        children: [
          { path: '/super', element: <SuperDashboardPage /> },
          { path: '/super/companies', element: <SuperCompaniesListPage /> },
          { path: '/super/companies/new', element: <SuperCompanyCreatePage /> },
          {
            path: '/super/companies/:enc_id',
            element: <SuperCompanyDetailPage />,
          },
          {
            path: '/super/companies/:enc_id/storage',
            element: <SuperCompanyStoragePage />,
          },
          { path: '/super/admins', element: <SuperAdminsListPage /> },
          { path: '/super/admins/new', element: <SuperAdminCreatePage /> },
          { path: '/super/system', element: <SuperSystemPage /> },
          { path: '/super/logs', element: <SuperLogsPage /> },
          { path: '/super/wb-activity', element: <SuperWbActivityPage /> },
          { path: '/super/product-groups', element: <SuperProductGroupsPage /> },
        ],
      },
    ],
  },

  // -- 404 (no shell) -------------------------------------------------------
  { path: '*', element: <NotFoundPage /> },
    ],
  },
], {
  // Opt into v7 behaviour ahead of the upgrade. `v7_startTransition` lives
  // on `<RouterProvider>` (see App.tsx); `v7_relativeSplatPath` is the
  // router-level flag silenced here.
  future: {
    v7_relativeSplatPath: true,
  },
});

// Standalone variant for public routes — keeps the same look but without shell.
function ComingSoonStandalone({ name }: { name: string }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        background: 'var(--color-page-bg, #f8fafc)',
        color: 'var(--color-text-primary, #0f172a)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{name}</h1>
        <p style={{ color: 'var(--color-text-secondary, #475569)' }}>
          Under construction. This page lands in a future wave.
        </p>
      </div>
    </div>
  );
}
